import React, { useState, useEffect, useRef } from "react";
import { auth } from "./utils/firebase";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import logo from "./assets/featherglass-sq-logo.png";
import { companyName, versionNum } from "./globals";
import { getFirebaseErrorMessage } from "./utils/errorHandler";
import {
  consumePasswordResetCode,
  isSyntheticDevRuntime,
  sanitizeAuthActionError,
} from "./utils/emailVerification";
import { 
  validatePassword, 
  getPasswordRequirements, 
  getPasswordStrength, 
  getPasswordStrengthLabel 
} from "./utils/passwordValidator";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { CircleCheck, CircleX, Eye, EyeClosed } from 'lucide-react';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const resetCodeRef = useRef(undefined);
  if (resetCodeRef.current === undefined) {
    resetCodeRef.current = consumePasswordResetCode({ searchParams });
  }
  const oobCode = resetCodeRef.current ?? (!isSyntheticDevRuntime() ? searchParams.get('oobCode') : null);

  // Form state
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  
  // Validation states
  const [passwordValidation, setPasswordValidation] = useState({ 
    isValid: null, 
    message: "", 
    strength: 0 
  });
  const [confirmPasswordValidation, setConfirmPasswordValidation] = useState({ 
    isValid: null, 
    message: "" 
  });
  
  // UI states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("error");
  
  // Refs for auto-focus
  const passwordRef = useRef(null);
  const confirmPasswordRef = useRef(null);

  // Verify the reset code on mount
  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode) {
        setStatus('error');
        setMessage('Invalid password reset link. Please request a new password reset from the sign-in page.');
        setTimeout(() => navigate('/'), 5000);
        return;
      }

      try {
        const userEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(userEmail);
        setStatus('idle');
        setTimeout(() => passwordRef.current?.focus(), 100);
      } catch (error) {
        resetCodeRef.current = null;
        console.error('Error verifying reset code:', sanitizeAuthActionError(error));
        setStatus('error');
        
        if (error.code === 'auth/invalid-action-code') {
          setMessage('This password reset link is invalid or has already been used. Please request a new one from the sign-in page.');
        } else if (error.code === 'auth/expired-action-code') {
          setMessage('This password reset link has expired. Please request a new one from the sign-in page.');
        } else {
          setMessage('Unable to verify password reset link. Please request a new one from the sign-in page.');
        }
        
        setTimeout(() => navigate('/'), 5000);
      }
    };

    verifyCode();
  }, [oobCode, navigate]);

  // Real-time password validation
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);

    if (!value) {
      setPasswordValidation({ isValid: null, message: "", strength: 0 });
      return;
    }

    const strength = getPasswordStrength(value, email);
    const isValid = validatePassword(value);
    const strengthInfo = getPasswordStrengthLabel(strength);

    let validationMessage = "";
    if (!isValid) {
      const requirements = getPasswordRequirements(value);
      const unmetReqs = requirements.filter((req) => !req.met);
      if (unmetReqs.length > 0) {
        validationMessage = `Missing: ${unmetReqs.map((req) => req.message.toLowerCase()).join(", ")}`;
      }
    } else {
      validationMessage = `${strengthInfo.label} password`;
    }

    setPasswordValidation({ isValid, message: validationMessage, strength });

    if (confirmPassword) {
      handleConfirmPasswordChange({ target: { value: confirmPassword } });
    }
  };

  // Real-time confirm password validation
  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);

    if (!value) {
      setConfirmPasswordValidation({ isValid: null, message: "" });
      return;
    }

    const isValid = value === password;
    const validationMessage = isValid ? "Passwords match" : "Passwords do not match";

    setConfirmPasswordValidation({ isValid, message: validationMessage });
  };

  // Handle Enter key navigation
  const handleKeyPress = (e, nextRef, action) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (action) {
        action();
      } else if (nextRef && nextRef.current) {
        nextRef.current.focus();
      }
    }
  };

  // Handle password reset submission
  const handleResetPassword = async () => {
    if (!passwordValidation.isValid) {
      setError("Please enter a valid password that meets all requirements.");
      passwordRef.current?.focus();
      return;
    }

    if (!confirmPasswordValidation.isValid) {
      setError("Passwords do not match. Please try again.");
      confirmPasswordRef.current?.focus();
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      const activeOobCode = resetCodeRef.current ?? (!isSyntheticDevRuntime() ? searchParams.get('oobCode') : null);
      if (!activeOobCode) {
        setStatus('error');
        setMessage('Invalid password reset link. Please request a new password reset from the sign-in page.');
        setTimeout(() => navigate('/'), 5000);
        return;
      }
      await confirmPasswordReset(auth, activeOobCode, password);
      resetCodeRef.current = null;
      setStatus('success');
      setSuccess("Password reset successfully! Redirecting you to sign in...");
      
      setTimeout(() => {
        navigate('/?reset=success');
      }, 3000);
      
    } catch (error) {
      console.error('Error resetting password:', sanitizeAuthActionError(error));
      setStatus('idle');
      
      if (error.code === 'auth/invalid-action-code') {
        resetCodeRef.current = null;
        setError('This password reset link is invalid or has already been used. Please request a new one from the sign-in page.');
        setTimeout(() => navigate('/'), 5000);
      } else if (error.code === 'auth/expired-action-code') {
        resetCodeRef.current = null;
        setError('This password reset link has expired. Please request a new one from the sign-in page.');
        setTimeout(() => navigate('/'), 5000);
      } else if (error.code === 'auth/weak-password') {
        setError('Password does not meet security requirements. Please choose a stronger password.');
      } else if (error.code === 'auth/user-disabled') {
        setError('This account has been disabled. Please contact support for assistance.');
      } else if (error.code === 'auth/user-not-found') {
        setError('Account not found. The account may have been deleted.');
      } else {
        const friendlyMessage = getFirebaseErrorMessage(error, 'passwordReset');
        setError(friendlyMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Helper functions
  const setError = (msg) => {
    setMessage(msg);
    setMessageType("error");
  };

  const setSuccess = (msg) => {
    setMessage(msg);
    setMessageType("success");
  };

  return (
    <div className="login-container">
      <img src={logo} alt="Logo" className="logo-login" />
      
      {status === 'verifying' && (
        <>
          <h2 className="title-login">Verifying Reset Link</h2>
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <CircularProgress size={48} style={{ marginBottom: 16 }} />
            <p>Please wait while we verify your password reset link...</p>
          </div>
        </>
      )}

      {status === 'success' && (
        <>
          <h2 className="title-login">Password Reset Complete!</h2>
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <CircleCheck style={{ color: 'var(--color-success)', marginBottom: 16 }} size={48} />
            <p className="login-success-message">{message}</p>
          </div>
        </>
      )}

      {status === 'error' && (
        <>
          <h2 className="title-login">Reset Link Invalid</h2>
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <CircleX style={{ color: 'var(--color-error)', marginBottom: 16 }} size={48} />
            <p className="login-error-message">{message}</p>
            <p style={{ marginTop: 16, fontSize: 'var(--text-sm)', color: 'var(--color-text-light)' }}>
              Redirecting you to the sign-in page...
            </p>
          </div>
        </>
      )}

      {status === 'idle' && (
        <>
          <h2 className="title-login">Reset Your Password</h2>
          <p className="subtitle">Enter your new password below</p>
          {email && <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-light)', marginBottom: '1rem' }}>for {email}</p>}

          <div className="input-container">
            <input 
              ref={passwordRef}
              type={showPassword ? "text" : "password"}
              value={password} 
              onChange={handlePasswordChange}
              onKeyDown={(e) => handleKeyPress(e, confirmPasswordRef)}
              placeholder="New Password" 
              className={`main-input ${
                passwordValidation.isValid === false ? 'input-error' : 
                passwordValidation.isValid === true ? 'input-success' : ''
              }`}
              disabled={isLoading}
            />
            <div className="input-validation">
              <Tooltip title={showPassword ? "Hide password" : "Show password"} arrow>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="password-toggle"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeClosed /> : <Eye />}
                </button>
              </Tooltip>
              {passwordValidation.isValid === true && <CircleCheck className="validation-icon success" />}
              {passwordValidation.isValid === false && <CircleX className="validation-icon error" />}
            </div>
          </div>

          {password && (
            <div className="password-strength">
              <div className="strength-bar">
                <div 
                  className={`strength-fill strength-${passwordValidation.strength}`} 
                  style={{ width: `${(passwordValidation.strength / 8) * 100}%` }}
                />
              </div>
              <p className={`validation-message ${passwordValidation.isValid ? "success" : "error"}`}>
                {passwordValidation.message}
              </p>
            </div>
          )}

          <div className="input-container">
            <input 
              ref={confirmPasswordRef}
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword} 
              onChange={handleConfirmPasswordChange}
              onKeyDown={(e) => handleKeyPress(e, null, handleResetPassword)}
              placeholder="Confirm New Password" 
              className={`main-input ${
                confirmPasswordValidation.isValid === false ? 'input-error' : 
                confirmPasswordValidation.isValid === true ? 'input-success' : ''
              }`}
              disabled={isLoading}
            />
            <div className="input-validation">
              <Tooltip title={showConfirmPassword ? "Hide password" : "Show password"} arrow>
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="password-toggle"
                  disabled={isLoading}
                >
                  {showConfirmPassword ? <EyeClosed /> : <Eye />}
                </button>
              </Tooltip>
              {confirmPasswordValidation.isValid === true && <CircleCheck className="validation-icon success" />}
              {confirmPasswordValidation.isValid === false && <CircleX className="validation-icon error" />}
            </div>
          </div>

          {confirmPassword && confirmPasswordValidation.message && (
            <p className={`validation-message ${confirmPasswordValidation.isValid ? "success" : "error"}`}>
              {confirmPasswordValidation.message}
            </p>
          )}

          {message && <p className={`${messageType === "error" ? "login-error-message" : "login-success-message"}`}>{message}</p>}
          
          <button 
            onClick={handleResetPassword} 
            className="center-button primary"
            disabled={isLoading || !passwordValidation.isValid || !confirmPasswordValidation.isValid}
          >
            {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Reset Password'}
          </button>

          <div className="auth-links" style={{ marginTop: '1rem' }}>
            <button 
              onClick={() => navigate('/')}
              className="link-button"
              disabled={isLoading}
            >
              Back to Sign In
            </button>
          </div>
        </>
      )}
      
      <footer className="footer">
        <p>
          &copy; {new Date().getFullYear()} {companyName} v{versionNum}
          <br />
          All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default ResetPassword;
